import type { RootState } from "@reduxjs/toolkit/dist/query/core/apiState";
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { HYDRATE } from "next-redux-wrapper";
import { stringify } from "qs";
import type { AnyAction } from "redux";
import type { ThunkDispatch } from "redux-thunk";

import type { ImportSongData } from "db/operations/song/importSongs";
import type { JoinedPlayedSongData } from "db/operations/song/userPlayedSongs";
import type { PublicUser } from "db/operations/user/listUsers";
import type { ArtistClient } from "db/schemas/artist";
import type { UserFriend } from "db/schemas/userFriend";
import type { UserSong } from "db/schemas/userSong";
import type { ArtistsState } from "features/artistsSlice";
import { selectToken } from "features/auth/authSlice";
import type { SongsState } from "features/songs/songsSlice";
import { songsActions } from "features/songs/songsSlice";
import type { UsersState } from "features/usersSlice";
import type { PlayedSongRequestData } from "pages/api/playedSong";
import type { ShareSongRequestData } from "pages/api/shareSong";
import type { AppState } from "store/store";
import type { SharedType } from "util/sharedType";
import type { SortDirection } from "util/sort";

import { providesList } from "./providesList";

export type { PlayedSongRequestData };
export type Patch<T extends { id: any }> = Partial<T> & Pick<T, "id">;

// Define a service using a base URL and expected endpoints
export const api = createApi({
  reducerPath: "api",
  baseQuery: fetchBaseQuery({
    // Need absolute URL for SSR
    baseUrl: `${process.env.NEXT_PUBLIC_BASE_URL}/api`,
    prepareHeaders: (headers, { getState }) => {
      const token = selectToken(getState() as AppState);
      // If we have a token set in state, let's assume that we should be passing it.
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ["Artists", "Chart", "Songs", "Users"],
  extractRehydrationInfo(action, { reducerPath }) {
    if (action.type === HYDRATE) {
      return action.payload[reducerPath];
    }
  },
  endpoints: (builder) => ({
    dislikeSong: builder.mutation<UserSong, string>({
      query: (id) => ({
        url: "dislikeSong",
        method: "POST",
        body: { id },
      }),
      async onQueryStarted(_id, { dispatch, queryFulfilled, getState }) {
        const { data: updatedSong } = await queryFulfilled;
        songUpdated({ updatedSong, dispatch, getState });
      },
    }),
    librarySong: builder.mutation<UserSong, string>({
      query: (id) => ({
        url: "librarySong",
        method: "POST",
        body: { id },
      }),
      async onQueryStarted(_id, { dispatch, queryFulfilled, getState }) {
        const { data: updatedSong } = await queryFulfilled;
        songUpdated({ updatedSong, dispatch, getState });
      },
      invalidatesTags: [{ type: "Songs", id: "LIST" }],
    }),
    likeSong: builder.mutation<UserSong, string>({
      query: (id) => ({
        url: "likeSong",
        method: "POST",
        body: { id },
      }),
      async onQueryStarted(_id, { dispatch, queryFulfilled, getState }) {
        const { data: updatedSong } = await queryFulfilled;
        songUpdated({ updatedSong, dispatch, getState });
      },
    }),
    shareSong: builder.mutation<UserSong, ShareSongRequestData>({
      query: (body) => ({
        url: "shareSong",
        method: "POST",
        body,
      }),
      async onQueryStarted(id, { dispatch, queryFulfilled, getState }) {
        const { data: updatedSong } = await queryFulfilled;
        songUpdated({ updatedSong, dispatch, getState });
      },
    }),
    importSongs: builder.mutation<void, ImportSongData>({
      query: (body) => ({
        url: `uploadSongs/import`,
        method: "POST",
        body,
      }),
      // Invalidates all Artist/Song-type queries providing the `LIST` id - after all, depending of the sort order,
      // that newly created post could show up in any lists.
      invalidatesTags: [
        { type: "Artists", id: "LIST" },
        { type: "Songs", id: "LIST" },
      ],
    }),
    playedSong: builder.mutation<UserSong, PlayedSongRequestData>({
      query: (body) => ({
        url: "playedSong",
        method: "POST",
        body,
      }),
      async onQueryStarted({ songId }, { dispatch, queryFulfilled, getState }) {
        const { data: updatedSong } = await queryFulfilled;
        songUpdated({ updatedSong, dispatch, getState });
      },
    }),
    getChart: builder.query<Array<UserSong>, void>({
      query: () => {
        return `chart`;
      },
      providesTags: (result) => providesList(result, "Chart"),
    }),
    getSharedSongs: builder.query<
      Array<UserSong>,
      Pick<SongsState, "search"> & {
        sharedType: SharedType;
      }
    >({
      query: ({ search, sharedType }) => {
        const query = {
          filter: { q: search },
          sharedType,
        };
        return `userSharedSongs?${stringify(query)}`;
      },
      providesTags: (result) => providesList(result, "Songs"),
    }),
    getSongs: builder.query<
      Array<UserSong>,
      Pick<SongsState, "search" | "sortBy" | "sortDirection"> & {
        filter?: { artistId: string };
        libraryBalance?: number;
      }
    >({
      query: ({ filter, libraryBalance, search, sortBy, sortDirection }) => {
        const query = {
          // limit: perPage,
          // offset: rangeStart,
          sortBy,
          sortDirection,
          filter: { ...filter, q: search },
          libraryBalance,
        };
        return `userSongs?${stringify(query)}`;
      },
      providesTags: (result) => providesList(result, "Songs"),
    }),
    getPlayedSongs: builder.query<
      Array<JoinedPlayedSongData>,
      {
        search: string;
        sortBy: keyof JoinedPlayedSongData;
        sortDirection: SortDirection;
        joinRelations?: string;
      }
    >({
      query: ({ search, sortBy, sortDirection, joinRelations }) => {
        const query = {
          sortBy,
          sortDirection,
          filter: { q: search },
          joinRelations,
        };
        return `userPlayedSongs?${stringify(query)}`;
      },
    }),
    getArtists: builder.query<
      Array<ArtistClient>,
      Pick<ArtistsState, "search" | "sortBy" | "sortDirection">
    >({
      query: ({ search, sortBy, sortDirection }) => {
        const query = {
          sortBy,
          sortDirection,
          filter: { q: search },
        };
        return `artists?${stringify(query)}`;
      },
      providesTags: (result) => providesList(result, "Artists"),
    }),
    getArtist: builder.query<ArtistClient, string>({
      query: (id) => {
        return `artist/${id}`;
      },
      providesTags: (_result, _error, id) => [{ type: "Artists", id }],
    }),
    updateArtist: builder.mutation<ArtistClient, Patch<ArtistClient>>({
      query: ({ id, ...patch }) => ({
        url: `artist/${id}`,
        method: "PATCH",
        body: patch,
      }),
      // Invalidates all queries that subscribe to this Artist `id` only.
      // `getArtist` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { id }) => [{ type: "Artists", id }],
    }),
    getFriends: builder.query<Array<PublicUser>, void>({
      query: () => {
        return "userFriends";
      },
      providesTags: (result) => providesList(result, "Users"),
    }),
    createFriend: builder.mutation<UserFriend, { id: string }>({
      query: ({ id }) => ({
        url: "userFriend",
        method: "POST",
        body: { id: id },
      }),
    }),
    deleteFriend: builder.mutation<void, string>({
      query: (id) => ({
        url: `userFriend/${id}`,
        method: "DELETE",
      }),
      // Invalidates all queries that subscribe to this User `id` only.
      // `getUsers` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, id) => [{ type: "Users", id }],
    }),
    getUser: builder.query<PublicUser, string>({
      query: (id) => {
        return `user/${id}`;
      },
      providesTags: (_result, _error, id) => [{ type: "Users", id }],
    }),
    getUsers: builder.query<
      Array<PublicUser>,
      Pick<UsersState, "search" | "sortBy" | "sortDirection"> & {
        filter?: { artistId: string };
      }
    >({
      query: ({ filter, search, sortBy, sortDirection }) => {
        const query = {
          sortBy,
          sortDirection,
          filter: { ...filter, q: search },
        };
        return `users?${stringify(query)}`;
      },
      providesTags: (result) => providesList(result, "Users"),
    }),
    getNewestSongs: builder.query<Array<UserSong>, void>({
      query: () => "songs/newest",
      providesTags: (result) => providesList(result, "Songs"),
    }),
    getHotSongs: builder.query<Array<UserSong>, void>({
      query: () => "songs/hot",
      providesTags: (result) => providesList(result, "Songs"),
    }),
    getLibrarySongs: builder.query<Array<UserSong>, void>({
      query: () => ({
        url: "userSongs",
        params: { libraryBalance: 100 },
      }),
      providesTags: (result) => providesList(result, "Songs"),
    }),
  }),
});

// Manually update the query cache otherwise the song may move or disappear
// from the list.
async function songUpdated({
  updatedSong,
  dispatch,
  getState,
}: {
  updatedSong: UserSong;
  dispatch: ThunkDispatch<any, any, AnyAction>;
  getState: () => RootState<any, any, "api">;
}) {
  dispatch(songsActions.songUpdated(updatedSong));

  for (const { endpointName, originalArgs } of api.util.selectInvalidatedBy(
    getState(),
    [{ type: "Songs", id: updatedSong.id }]
  )) {
    // we only want to update `getSongs` here
    if (endpointName === "getSongs") {
      // Get the latest chart
      const chartAction = dispatch(
        api.endpoints.getChart.initiate(originalArgs, { forceRefetch: true })
      );
      chartAction.unsubscribe();
      const chartResult = await chartAction;
      const chart = chartResult.data;

      dispatch(
        api.util.updateQueryData(endpointName, originalArgs, (draft) => {
          // find in list & update
          for (const song of draft) {
            if (song.id === updatedSong.id) {
              Object.assign(song, updatedSong);
            }

            // Update chart positions.
            const songInChart = chart?.find((o) => o.id === song.id);
            if (songInChart) {
              song.currentChartPosition = songInChart.currentChartPosition;
            } else {
              delete song.currentChartPosition;
            }
          }
        })
      );
    }
  }
}

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useDislikeSongMutation,
  useLibrarySongMutation,
  useLikeSongMutation,
  useShareSongMutation,
  useImportSongsMutation,
  usePlayedSongMutation,
  useGetArtistsQuery,
  useGetArtistQuery,
  useGetSharedSongsQuery,
  useGetSongsQuery,
  useGetPlayedSongsQuery,
  useGetFriendsQuery,
  useCreateFriendMutation,
  useDeleteFriendMutation,
  useGetUserQuery,
  useGetUsersQuery,
  useUpdateArtistMutation,
  useGetNewestSongsQuery,
  useGetHotSongsQuery,
  useGetLibrarySongsQuery,
} = api;
